import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, ArrowLeft, CheckCircle, Mail } from "lucide-react";
import axios from "axios";
import { formatApiErrorDetail, useAuth } from "@/App";

const L = {
  bg: { title: "Забравена парола", desc: "Въведете имейла си и ще ви изпратим линк за нулиране на паролата.", email: "Имейл", send: "Изпрати линк", sending: "Изпращане...", back: "Назад към вход", success: "Ако имейлът е регистриран, линк за нулиране е изпратен. Проверете входящата си поща." },
  en: { title: "Forgot Password", desc: "Enter your email and we'll send you a password reset link.", email: "Email", send: "Send Reset Link", sending: "Sending...", back: "Back to Login", success: "If the email is registered, a reset link has been sent. Please check your inbox." },
  es: { title: "Contrasena olvidada", desc: "Ingresa tu correo y te enviaremos un enlace para restablecer tu contrasena.", email: "Correo", send: "Enviar enlace", sending: "Enviando...", back: "Volver al inicio de sesion", success: "Si el correo esta registrado, se ha enviado un enlace. Revise su bandeja de entrada." },
  de: { title: "Passwort vergessen", desc: "Geben Sie Ihre E-Mail ein und wir senden Ihnen einen Link zum Zurucksetzen.", email: "E-Mail", send: "Link senden", sending: "Senden...", back: "Zuruck zur Anmeldung", success: "Falls die E-Mail registriert ist, wurde ein Link gesendet. Bitte prufen Sie Ihren Posteingang." },
  ar: { title: "نسيت كلمة المرور", desc: "ادخل بريدك الالكتروني وسنرسل لك رابط اعادة تعيين كلمة المرور.", email: "البريد الالكتروني", send: "ارسال الرابط", sending: "جاري الارسال...", back: "العودة لتسجيل الدخول", success: "اذا كان البريد مسجلا، تم ارسال رابط اعادة التعيين. يرجى التحقق من بريدك." },
  zh: { title: "忘记密码", desc: "输入您的邮箱，我们将发送密码重置链接。", email: "邮箱", send: "发送重置链接", sending: "发送中...", back: "返回登录", success: "如果邮箱已注册，重置链接已发送。请检查您的收件箱。" },
};

export default function ForgotPassword() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const l = L[language] || L.en;
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-3 bg-[#0EA5E9]/10">
              <Mail className="w-8 h-8 text-[#0EA5E9]" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#1A1A2E] mb-2">{l.title}</h1>
            <p className="text-sm text-[#94A3B8] max-w-sm mx-auto">{l.desc}</p>
          </div>

          <Card className="card-elevated border-0">
            <CardContent className="p-6">
              {success ? (
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-[#10B981]" />
                  </div>
                  <p className="text-sm text-[#64748B]">{l.success}</p>
                  <Link to="/login" className="block">
                    <Button variant="outline" className="w-full h-11 rounded-xl text-sm" data-testid="back-to-login-btn">
                      <ArrowLeft className="w-4 h-4 mr-2" /> {l.back}
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="forgot-error">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#1A1A2E] text-sm font-medium">{l.email}</Label>
                    <Input id="email" type="email" placeholder="you@example.com" value={email}
                      onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input"
                      className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                    className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl shadow-lg shadow-sky-500/20">
                    {loading ? l.sending : l.send}
                  </Button>
                  <div className="text-center">
                    <Link to="/login" className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-testid="back-login-link">
                      <ArrowLeft className="w-3 h-3 inline mr-1" />{l.back}
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
